import type {
  FramingSceneInspector,
  FramingStageKey,
} from "../framingInspector";
import { clampCamera, type FramingViewportCamera } from "./framingViewportMath";

/**
 * Canonical workbench state (CROP-WB-006). URL/deep-link values are parsed
 * separately and remain untrusted hints until inspector validation; the
 * reducer owns the only authoritative selection.
 */

export type FramingOverlayPresetKey =
  | "subject_safety"
  | "plan_vs_applied"
  | "transform_lineage"
  | "effects"
  | "none"
  | "advanced";

export type FramingWorkbenchComparison = {
  attemptB: string;
  stage: FramingStageKey;
} | null;

export type FramingWorkbenchState = {
  open: boolean;
  sceneId: string | null;
  /** null selects the server-declared default attempt. */
  attemptId: string | null;
  layout: "quad" | "all" | "single";
  activeStage: FramingStageKey;
  overlayPreset: FramingOverlayPresetKey;
  timeSeconds: number;
  camera: FramingViewportCamera | null;
  linkViews: boolean;
  comparison: FramingWorkbenchComparison;
  /** Bumped on every scene/attempt change so late responses can be ignored. */
  selectionEpoch: number;
};

export const INITIAL_WORKBENCH_STATE: FramingWorkbenchState = {
  open: false,
  sceneId: null,
  attemptId: null,
  layout: "quad",
  activeStage: "encoded",
  overlayPreset: "subject_safety",
  timeSeconds: 0,
  camera: null,
  linkViews: false,
  comparison: null,
  selectionEpoch: 0,
};

export type FramingWorkbenchAction =
  | {
      type: "open";
      sceneId: string;
      attemptId?: string | null;
      stage?: FramingStageKey | null;
      timeSeconds?: number | null;
    }
  | { type: "close" }
  | { type: "select_scene"; sceneId: string }
  | { type: "select_attempt"; attemptId: string | null }
  | { type: "set_layout"; layout: FramingWorkbenchState["layout"] }
  | { type: "set_stage"; stage: FramingStageKey }
  | { type: "set_preset"; preset: FramingOverlayPresetKey }
  | { type: "set_time"; timeSeconds: number }
  | { type: "set_camera"; camera: FramingViewportCamera | null }
  | { type: "toggle_link_views"; enabled: boolean }
  | { type: "set_comparison"; comparison: FramingWorkbenchComparison }
  | {
      /** Signed-media refresh: everything investigative is preserved. */
      type: "media_refreshed";
    }
  | {
      /** The visible coordinate root changed; viewport resets, rest stays. */
      type: "root_changed";
      rootId: string;
    };

export function framingWorkbenchReducer(
  state: FramingWorkbenchState,
  action: FramingWorkbenchAction,
): FramingWorkbenchState {
  switch (action.type) {
    case "open":
      return {
        ...INITIAL_WORKBENCH_STATE,
        open: true,
        sceneId: action.sceneId,
        attemptId: action.attemptId ?? null,
        activeStage: action.stage ?? "encoded",
        timeSeconds:
          action.timeSeconds != null && Number.isFinite(action.timeSeconds)
            ? Math.max(0, action.timeSeconds)
            : 0,
        selectionEpoch: state.selectionEpoch + 1,
      };
    case "close":
      return {
        ...state,
        open: false,
        camera: null,
        comparison: null,
        selectionEpoch: state.selectionEpoch + 1,
      };
    case "select_scene":
      if (action.sceneId === state.sceneId) return state;
      return {
        ...state,
        sceneId: action.sceneId,
        attemptId: null,
        timeSeconds: 0,
        camera: null,
        comparison: null,
        selectionEpoch: state.selectionEpoch + 1,
      };
    case "select_attempt":
      if (action.attemptId === state.attemptId) return state;
      return {
        ...state,
        attemptId: action.attemptId,
        camera: null,
        comparison: null,
        selectionEpoch: state.selectionEpoch + 1,
      };
    case "set_layout":
      return { ...state, layout: action.layout };
    case "set_stage":
      return { ...state, activeStage: action.stage };
    case "set_preset":
      return { ...state, overlayPreset: action.preset };
    case "set_time":
      return {
        ...state,
        timeSeconds: Number.isFinite(action.timeSeconds)
          ? Math.max(0, action.timeSeconds)
          : state.timeSeconds,
      };
    case "set_camera":
      return {
        ...state,
        camera: action.camera ? clampCamera(action.camera) : null,
      };
    case "toggle_link_views":
      return { ...state, linkViews: action.enabled };
    case "set_comparison":
      return { ...state, comparison: action.comparison };
    case "media_refreshed":
      // Scene, attempt, stage, time, preset, and viewport all survive a
      // controlled signed-URL refresh (CROP-WB-006).
      return state;
    case "root_changed":
      if (state.camera?.rootId === action.rootId) return state;
      return { ...state, camera: null };
    default:
      return state;
  }
}

/** Untrusted navigation hints parsed from the URL (CROP-WB-006). */
export type FramingDeepLinkHints = {
  feedbackId: string | null;
  sceneId: string | null;
  attemptId: string | null;
  stage: FramingStageKey | null;
  timestampMs: number | null;
};

const STAGE_KEYS: readonly FramingStageKey[] = [
  "raw",
  "working",
  "provider",
  "generated",
  "pre_effect",
  "encoded",
];

export function parseFramingDeepLinkHints(
  search: string,
): FramingDeepLinkHints {
  const params = new URLSearchParams(search);
  const stageRaw = params.get("stage");
  const timestampRaw = params.get("feedbackTimestampMs");
  const timestamp = timestampRaw == null ? NaN : Number(timestampRaw);
  return {
    feedbackId: params.get("feedbackId"),
    sceneId: params.get("framingScene"),
    attemptId: params.get("attempt"),
    stage: (STAGE_KEYS as readonly string[]).includes(stageRaw ?? "")
      ? (stageRaw as FramingStageKey)
      : null,
    timestampMs:
      Number.isSafeInteger(timestamp) &&
      timestamp >= 0 &&
      timestamp <= 86_400_000
        ? timestamp
        : null,
  };
}

export type FramingHintValidation = {
  status: "valid" | "mismatch";
  warnings: string[];
};

/**
 * Compare URL hints with the authorized server evidence. A mismatch becomes
 * an explicit warning and never silently selects different evidence.
 */
export function validateFramingHints(
  hints: FramingDeepLinkHints,
  inspector: FramingSceneInspector,
): FramingHintValidation {
  const warnings: string[] = [];
  if (hints.sceneId != null && hints.sceneId !== inspector.sceneId) {
    warnings.push(
      `The link names scene ${hints.sceneId}, but the server returned scene ${inspector.sceneId}.`,
    );
  }
  if (hints.attemptId != null) {
    const knownAttempts = new Set([
      inspector.attemptId ?? "",
      inspector.current.attemptId ?? "",
      ...inspector.attempts.map((attempt) => attempt.attemptId),
    ]);
    if (!knownAttempts.has(hints.attemptId)) {
      warnings.push(
        `The link names attempt ${hints.attemptId}, which does not belong to this scene.`,
      );
    }
  }
  if (inspector.attemptAvailability === "correlation_mismatch") {
    warnings.push(
      "The requested attempt does not correlate with this scene; no other attempt was selected.",
    );
  }
  return { status: warnings.length > 0 ? "mismatch" : "valid", warnings };
}
